import React, { useState } from "react";
import { Maximize2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { projects } from "../../Data/projects";
import { skills } from "../../Data/skills";
import { GlowingEffect } from "../ui/glowing-effect";
import { Modal } from "../ui/Modal";
import { useLang } from "../../context/LanguageContext";
import { t } from "../../translations";

export default function Projects() {
    const [openProject, setOpenProject] = useState(null);
    const { lang } = useLang();
    const tr = t[lang].projects;

    // Helper to get icon component from skill name
    const getTechIcon = (techName) => {
        const skill = skills.find(s => s.name === techName);
        if (!skill) return null;
        const IconComponent = skill.icon;
        return <IconComponent className="w-5 h-5" />;
    };

    return (
        <section id="projects" className="w-full bg-surface transition-colors duration-200">
            <div className="max-w-7xl mx-auto pt-12 sm:pt-16 md:pt-24 px-4 sm:px-8 md:px-16 pb-12 sm:pb-16 md:pb-24">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8 text-center text-label">
                    {tr.title}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {projects.map((project, idx) => (
                        <Card
                            key={project.title}
                            className="relative bg-surface-elevated border border-separator rounded-3xl shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover cursor-pointer overflow-hidden group"
                            onClick={() => setOpenProject(idx)}
                        >
                            <GlowingEffect
                                spread={70}
                                glow={false}
                                disabled={false}
                                proximity={120}
                                inactiveZone={0.01}
                                borderWidth={3}
                                blur={4}
                            />
                            <Maximize2
                                aria-hidden="true"
                                className="pointer-events-none absolute right-5 top-5 z-20 h-4 w-4 text-label-tertiary opacity-60 transition-all duration-200 group-hover:opacity-100 group-hover:text-accent-text"
                            />
                            <CardContent className="p-6 flex flex-col h-full relative z-10">
                                <h3 className="text-xl font-semibold tracking-tight mb-2 pr-8 text-label group-hover:text-accent-text transition-colors">
                                    {project.title}
                                </h3>
                                <p className="mb-4 text-label-secondary flex-1 text-[15px] leading-relaxed">
                                    {project.description[lang]}
                                </p>

                                {/* Tech Stack Icons */}
                                <div className="mb-4 flex flex-wrap gap-2 items-center">
                                    <span className="text-[11px] font-semibold text-label-tertiary uppercase tracking-wider">
                                        {tr.techStack}
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {project.technologies.map((tech, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center gap-1 px-2 py-1 bg-black/[0.04] dark:bg-white/[0.06] rounded-lg text-label-secondary transition-colors"
                                                title={tech}
                                            >
                                                {getTechIcon(tech)}
                                                <span className="text-xs font-medium">{tech}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <a
                                    href={project.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-auto"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Button variant="link" className="text-[15px] font-medium">
                                        {tr.viewOnGithub}
                                    </Button>
                                </a>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Enhanced Modal for Project Details */}
                <Modal
                    open={typeof openProject === "number"}
                    onClose={() => setOpenProject(null)}
                    className="max-w-3xl my-8"
                    labelledBy="project-modal-title"
                >
                    {typeof openProject === "number" && (
                        <>
                            {/* Fixed Close Button */}
                            <button
                                className="absolute top-3 right-3 z-10 h-11 w-11 rounded-full flex items-center justify-center text-label-tertiary hover:text-label hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                onClick={() => setOpenProject(null)}
                                aria-label="Close"
                            >
                                <span className="text-2xl leading-none">×</span>
                            </button>

                            {/* Scrollable Content Container */}
                            <div className="max-h-[85vh] overflow-y-auto scrollbar-hide">
                                {/* Demo Image Placeholder */}
                                {projects[openProject].image && (
                                    <div className="w-full h-64 bg-gradient-to-br from-blue-500 to-purple-600 rounded-t-3xl overflow-hidden">
                                        <img
                                            src={projects[openProject].image}
                                            alt={projects[openProject].title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}

                                <div className="p-8">
                                    <h2 id="project-modal-title" className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 text-label pr-8">
                                        {projects[openProject].title}
                                    </h2>

                                    {/* Tech Stack in Modal */}
                                    <div className="mb-6 flex flex-wrap gap-2 items-center">
                                        <span className="text-[13px] font-semibold text-label-tertiary">
                                            {tr.builtWith}
                                        </span>
                                        {projects[openProject].technologies.map((tech, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-lg text-accent-text"
                                            >
                                                {getTechIcon(tech)}
                                                <span className="text-[13px] font-medium">{tech}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <p className="mb-4 text-label text-[17px] leading-relaxed">
                                        {projects[openProject].description[lang]}
                                    </p>

                                    <div className="mb-6 text-label-secondary text-[15px] whitespace-pre-line leading-relaxed">
                                        {projects[openProject].details[lang]}
                                    </div>

                                    <div className="flex gap-3 flex-wrap pb-2">
                                        <a
                                            href={projects[openProject].link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Button>{tr.viewOnGithub}</Button>
                                        </a>
                                        {projects[openProject].demo && (
                                            <a
                                                href={projects[openProject].demo}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <Button variant="outline">{tr.liveDemo}</Button>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </Modal>
            </div>
        </section>
    );
}
